import { Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import * as moment from 'moment';
import { CountryISO, NgxIntlTelInputComponent, SearchCountryField } from 'ngx-intl-tel-input';
import { InformationModalPage } from 'src/app/pages/authentication/site-code-entry/information-modal/information-modal';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AlertService } from '../../../services/alert.service';
import { AuthService } from '../../../services/auth.service';
import { LoadingService } from '../../../services/loading.service';
import { Gender, GENDERS } from '../../../types/Gender';
import { CLINICIAN, DISPLAY_DOB, FIFTY_FIVE_YEARS_AGO, HEALTH_CARE_PROVIDER, INPUT_DATE_FORMAT } from '../../../utils/constants';
import { ValidationUtil, Validator } from '../../../utils/validation';


export enum RegisterFieldsPage1 {
  FIRST = 'first',
  LAST = 'last',
  DOB = 'dob',
  GENDER = 'gender'
}

export enum RegisterFieldsPage2 {
  PHONE = 'phone',
  SITE = 'site',
}


type RegisterFormConfig = {
  [index in RegisterFieldsPage1 | RegisterFieldsPage2]: [string, Validator[]] | FormControl
};

@Component({
  selector: 'app-register',
  templateUrl: './register.page.html',
  styleUrls: ['./register.page.scss', '../authentication.scss']
})
export class RegisterPage implements OnInit {

  title = 'Register';
  registerForm: FormGroup;

  maxDateStr: string;
  CountryIso = CountryISO;
  SearchCountryField = SearchCountryField;

  phoneNumber: string = '';

  @ViewChild('phoneNumber', { static: true })
  phoneNumberEl!: ElementRef;

  isValid!: boolean;
  errorMessage!: string;
  normalizedPhoneNumber!: string;
  phone: any;
  dobDisplay: string = DISPLAY_DOB;
  healthCareProvider: string = HEALTH_CARE_PROVIDER;
  clinician: string = CLINICIAN;
  genders: Gender[] = GENDERS;
  selectedCountryISO = CountryISO.Australia;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public alertProvider: AlertService,
    private docTitle: Title,
    private formBuilder: FormBuilder,
    private modalCtrl: ModalController,
    private loadingProvider: LoadingService,
    private amplifyProvider: AuthService,
    private renderer: Renderer2,
    private analyticsService: AnalyticsService,
  ) {
    this.analyticsService.tagRoute('unauthenticated/register');
    this.maxDateStr = moment().startOf('day').format(INPUT_DATE_FORMAT);
    const defaultDateStr = FIFTY_FIVE_YEARS_AGO;

    const formConfig: RegisterFormConfig = {
      first: ['', [ValidationUtil.isRequired]],
      last: ['', [ValidationUtil.isRequired]],
      dob: [
        defaultDateStr,
        [ValidationUtil.isRequired, ValidationUtil.dateInPast]
      ],
      gender: ['', [ValidationUtil.isRequired]],
      phone: ['', [ValidationUtil.isRequired]],
      site: ['', [ValidationUtil.isRequired]]
    };

    this.docTitle.setTitle(this.title);
    this.registerForm = this.formBuilder.group(formConfig);
  }

  ng2TelCustomOptions = {
    initialCountry: 'au',
    utilsScript: 'utils.js',
    customPlaceholder: (selectedCountryPlaceholder: any) => {
      return 'e.g. ' + selectedCountryPlaceholder;
    },
  };

  ionViewDidEnter() {
    this.phoneNumberEl.nativeElement.dispatchEvent(new Event('keyup'));
    const phone = this.renderer.selectRootElement('input[ng2telinput]');
    setTimeout(() => {
      if (phone) {
        phone.focus();
      }
    }, 1000);
  }

  async openInformationModal() {
    const modal = await this.modalCtrl.create({
      component: InformationModalPage,
    });
    await modal.present();
  }

  ngOnInit() {
    this.selectedCountryISO = CountryISO.Australia;
    this.route.queryParamMap.subscribe(params => {
      const siteCode = params.get('siteCode');
      const locale = params.get('locale');
      if (locale) {
        const countryCode = this.getCountryIsoFromLocale(locale);
        const foundCountryCode = Object.values(CountryISO).find(cc => cc === countryCode?.toLowerCase());
        if (foundCountryCode) {
          this.selectedCountryISO = foundCountryCode;
        }
      }
      if (siteCode) {
        this.registerForm.controls['site'].setValue(siteCode);
      }
    });
  }

  private getCountryIsoFromLocale(locale: string): string | null {
    try {
      const intlLocale = new Intl.Locale(locale);
      return intlLocale.region || null;
    } catch (error) {
      return null;
    }
  }


  submitForm(): void {
    this.normalizedPhoneNumber = this.phoneNumber ?? '';
    const singleUsePassword = this.amplifyProvider.generateNewPassword();
    this.loadingProvider.loadWhile(async (stopLoading) => {
      try {
        const formData = this.registerForm.value;
        const birthdate = moment(formData.dob).format('YYYY-MM-DD');
        const registerData = {
          phone_number: this.normalizedPhoneNumber,
          password: singleUsePassword,
          given_name: formData.first.trim(),
          family_name: formData.last.trim(),
          birthdate,
          gender: formData.gender,
          'custom:sitecode': formData.site.toLowerCase().trim()
        };
        await this.amplifyProvider.registerUser(registerData);
        if (Capacitor.isNativePlatform()) {
          this.router.navigate(['unauthenticated', 'verifysignup'], { queryParams: { phone: this.normalizedPhoneNumber } });
        } else {
          await this.amplifyProvider.sendCustomAuthSmsCode(this.normalizedPhoneNumber);
          this.router.navigate(['unauthenticated', 'verifysignup'], { queryParams: { phone: this.normalizedPhoneNumber } });
        }

      } catch (error) {
        this.handleError(error);
      } finally {
        stopLoading();
      }
    });
  }

  handleError(error: any) {
    if (this.amplifyProvider.isUsernameExistsException(error)) {
      this.handleUsernameExistsException(error);
    } else if (this.amplifyProvider.isUserLambdaValidationException(error)) {
      this.handleValidationException(error);
    } else {
      this.alertProvider.showAmplifyAuthError(error);
    }
  }

  handleUsernameExistsException(error: any) {
    console.error(error);
    const registeredPhoneNumber = this.registerForm.controls['phone'].value;
    this.alertProvider.confirm(
      `This mobile number is already registered`,
      `Check your number and try again. Alternative contact support@cardihab.com`,
      `Go to Login`,
      () => {
        this.router.navigate(['unauthenticated', 'login'], {
          queryParams: { registeredPhoneNumber }
        });
      }
    );
  }

  handleValidationException(error: any) {
    console.error(error);
    this.alertProvider.confirm(
      `Provider site not found`,
      `Please ask your ${this.healthCareProvider} or ${this.clinician} for this information.`
    );
  }

  setPhoneNumber($event: any) {
    if ($event) {
      this.phoneNumber = $event.e164Number ?? $event.internationalNumber ?? '';
      this.setErrorMessage(this.phoneNumber);
    }
  }


  setErrorMessage(phoneNumber: string) {
    if (!Boolean(phoneNumber)) {
      this.errorMessage = 'Mobile number is required';
      this.registerForm.controls['phone'].setErrors({ 'required': true });
    } else if (!this.registerForm.controls['phone'].valid) {
      this.errorMessage = 'Mobile number is not valid';
      this.registerForm.controls['phone'].setErrors({ 'invalid': true });
    } else {
      this.errorMessage = '';
    }
  }


  cancel() {
    if (this.registerForm.dirty) {
      this.showDiscardConfirmation();
    } else {
      this.analyticsService.tagButtonPress('registration', 'cancel');
      this.router.navigate(['unauthenticated', 'home']);
    }
  }

  showDiscardConfirmation(): void {
    this.alertProvider.confirmDiscard(
      'Discard unsaved changes?',
      'Do you want to discard unsaved changes?',
      'Discard',
      () => {
        this.analyticsService.tagButtonPress('registration', 'cancel');
        this.router.navigate(['unauthenticated', 'home']);
      }
    );
  }

}
